import { useEffect, useMemo, useState } from 'react'
import { ContainerStyled } from './styles'
import {
  getComission,
  sendTokens,
} from '../../redux/actions/transactions.actions'
import { Button } from '../../shared/button/button'
import { LinkStyled, NormalText, TextFieldStyled } from '../../shared/styles'
import { Dropdown } from '../../shared/dropdown/dropdown'
import { mockedTokensIcons, networks } from '../mockedData'
import { useDispatch, useSelector } from 'react-redux'
import { Box, useTheme } from '@mui/material'
import { Announcement } from '../../shared/announcement/announcement'
import { getAgenda } from '../../redux/actions/agenda.actions'
import { ConfirmModal } from '../../shared/confirm-modal/confirm-modal'
import { getFee } from './multiple-payments.utils'
import { Loader } from '../../shared/loader/loader'
import { getCoinsByNetwork, getCrossNetworkCoins, INITIAL_FIELDS } from './single-payment.utils'
import { DividerStyled } from '../../shared/divider/divider'
import { SuccessfullMessage } from '../bridge/successfull-message'

export const SinglePaymentContainer = ({
  user,
  shareWallet,
  clientApiKey,
  polygonAddress,
  tronAddress,
}) => {
  const [fields, setFields] = useState(INITIAL_FIELDS)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [errorFields, setErrorFields] = useState('')

  const {
    newTransaction: {
      data: newTransaction,
      isLoading: isLoadingNewTransaction,
      isError: isErrorNewTransaction,
    },
  } = useSelector((state) => state.transactions)

  const { data: agenda, isLoading: isLoadingAgenda } = useSelector(
    (state) => state.agenda
  )
  const { data: comission, isLoading: isLoadingComission } = useSelector(
    (state) => state.transactions.comission
  )

  const dispatch = useDispatch()
  const theme = useTheme()

  const agendaFormatted = agenda.map((contact) => ({
    value: contact.walletadress,
    label: `${contact?.alias} - ${contact?.nombre} ${contact?.apellido || ''}`,
  }))

  const coinsByNetwork = useMemo(() => {
    if (fields?.network !== fields?.destinationNetwork) {
      return getCrossNetworkCoins()
    }
    return getCoinsByNetwork(fields?.network)
  }, [fields?.network, fields?.destinationNetwork])

  function handleSendTokens() {
    dispatch(
      sendTokens({
        shareWallet,
        amount: fields?.amount,
        destinationWallet: fields?.destinationWallet,
        network: fields?.network,
        destinationNetwork: fields?.destinationNetwork,
        coin: fields?.coin,
        clientApiKey,
        fromPubkey:
          fields?.network?.toLowerCase() === 'polygon'
            ? polygonAddress
            : tronAddress,
      })
    )
    setShowConfirmModal(false)
  }

  function handleChange(e) {
    setFields({ ...fields, [e.target.name]: e.target.value })
  }

  function handleContactSelected(walletAddress) {
    setFields({
      ...fields,
      destinationWallet: walletAddress,
    })
  }

  function handleCloseConfirmModal() {
    setShowConfirmModal(false)
  }

  function handleOpenConfirmModal() {
    if (!fields?.amount || parseFloat(fields?.amount) <= 0 || !fields?.destinationWallet || !fields?.coin) {
      setErrorFields('Please fill all fields')
      return
    }
    setShowConfirmModal(true)
    setErrorFields('')
  }

  useEffect(() => {
    if (user?.usuarioid) {
      dispatch(getAgenda(user?.usuarioid, 1, 100000))
    }
  }, [dispatch, user?.usuarioid])

  useEffect(() => {
    dispatch(getComission())
  }, [dispatch])

  useEffect(() => {
    if (newTransaction?.signedTxNet || newTransaction?.sendusdtPolygon) {
      setFields(INITIAL_FIELDS)
    }
  }, [isLoadingNewTransaction, newTransaction])

  return (
    <>
      <ConfirmModal
        isOpen={showConfirmModal}
        handleClose={handleCloseConfirmModal}
        onConfirm={handleSendTokens}
        onCancel={handleCloseConfirmModal}
        title={'Confirm transaction'}
        children={
          <Box display={'flex'} flexDirection={'column'} gap={'8px'}>
            <Announcement
              description={
                'Tenui is not responsible if you make a mistake with any data (address, amount, network, etc.). It is your responsibility to verify that all transaction data is correct. Once confirmed, the transaction cannot be canceled and funds cannot be recovered if there are errors.'
              }
              withInfoIcon
            />
            <Announcement
              description={
                <Box display={'flex'} flexDirection={'column'} gap={'8px'}>
                  <NormalText isBold> Amount: {fields?.amount}</NormalText>
                  <NormalText isBold> From network: {fields?.network}</NormalText>
                  <NormalText isBold> Destination network: {fields?.destinationNetwork}</NormalText>
                  <NormalText
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    isBold
                  >
                    Coin:
                    <img
                      src={
                        mockedTokensIcons.find(
                          (token) => token.name === fields?.coin
                        )?.logo
                      }
                      alt={fields?.coin}
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '100%',
                      }}
                    />
                    {fields?.coin}
                  </NormalText>
                  <NormalText isBold>
                    Destination wallet: {fields?.destinationWallet}
                  </NormalText>
                </Box>
              }
            />
          </Box>
        }
      />
      <ContainerStyled>
        {isLoadingAgenda || isLoadingComission ? (
          <Loader />
        ) : (
          <>
            <Announcement
              description={
                'If the destination wallet is on a different network, an automatic swap will be performed, which may result in higher fees. Currently we only support USDT as a cross-network coin.'
              }
              withInfoIcon
            />
            <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
              <Box display={'flex'} gap={'4px'}>
                <Dropdown
                  label={'Network'}
                  withSearch={false}
                  options={networks}
                  defaultValue={fields.network}
                  onSelect={(network) => {
                    const newCoinsByNetwork = getCoinsByNetwork(network)
                    setFields({
                      ...fields,
                      network,
                      coin: fields.destinationNetwork !== network ? 'USDT' : newCoinsByNetwork[0]?.value,
                    })
                  }}
                  styles={{
                    flex: 1,
                  }}
                />
                <Dropdown
                  label={'Destination network'}
                  withSearch={false}
                  options={networks}
                  defaultValue={fields.destinationNetwork}
                  onSelect={(destinationNetwork) => {
                    setFields({
                      ...fields,
                      destinationNetwork,
                      coin: destinationNetwork !== fields.network ? 'USDT' : fields.coin,
                    })
                  }}
                  styles={{
                    flex: 1,
                  }}
                />
              </Box>
              <Dropdown
                label={'Coin'}
                withSearch={false}
                options={coinsByNetwork}
                defaultValue={fields.coin}
                onSelect={(coin) => setFields({ ...fields, coin })}
              />
              {agenda.length > 0 && (
                <Dropdown
                  label={'Contact'}
                  withSearch={false}
                  options={agendaFormatted}
                  onSelect={handleContactSelected}
                  defaultValue={fields?.destinationWallet}
                />
              )}
              <TextFieldStyled
                name={'destinationWallet'}
                placeholder={'Destination wallet'}
                fullWidth
                onChange={handleChange}
                value={fields?.destinationWallet}
              />
              <TextFieldStyled
                name={'amount'}
                placeholder={'Amount (Example: 1.01)'}
                fullWidth
                onChange={handleChange}
                value={fields?.amount}
              />
            </Box>
            <DividerStyled sx={{ my: '8px' }} />
            <Box
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'space-between'}
              gap={'8px'}
              border={`1px solid ${theme.palette.grey[1]}`}
              padding={'8px'}
              borderRadius={'8px'}
            >
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
                gap={'4px'}
              >
                <NormalText>Comission</NormalText>
                <NormalText>{comission}%</NormalText>
              </Box>
              <Box
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
                gap={'4px'}
              >
                <NormalText>Fee</NormalText>
                <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                  <img
                    src={
                      mockedTokensIcons.find(
                        (token) => token.name === fields?.coin
                      )?.logo
                    }
                    alt={fields?.coin}
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '100%',
                    }}
                  />
                  <NormalText>
                    {getFee(fields?.amount, comission)} {fields?.coin}
                  </NormalText>
                </Box>
              </Box>
            </Box>
            {errorFields && (
              <Box display={'flex'} flexDirection={'column'} gap={'8px'}>
                <NormalText color={'#EA7A7A'}>{errorFields}</NormalText>
              </Box>
            )}
            {isErrorNewTransaction && (
              <Box
                display={'flex'}
                flexDirection={'column'}
                gap={'8px'}
                justifyContent={'center'}
                my={'8px'}
              >
                <NormalText color={'#EA7A7A'}>
                  {newTransaction?.details || newTransaction?.error}
                </NormalText>
                {newTransaction?.balance && newTransaction?.required && (
                  <NormalText color={'#EA7A7A'}>
                    Your current balance is {newTransaction?.balance} and you
                    need {newTransaction?.required}
                  </NormalText>
                )}
              </Box>
            )}
            {(newTransaction?.signedTx ||
              newTransaction?.signedTransaction) && (
                <Announcement
                  title={'Transaction signed'}
                  description={
                    <LinkStyled
                      href={`https://polygonscan.com/tx/${newTransaction?.signedTx ||
                        newTransaction?.signedTransaction
                        }`}
                      target="_blank"
                    >
                      View on Polygonscan
                    </LinkStyled>
                  }
                  type={'info'}
                />
              )}
            {(newTransaction?.comision ||
              newTransaction?.neto ||
              newTransaction?.signedTxNet ||
              newTransaction?.sendusdtPolygon) &&
              <SuccessfullMessage />
            }
            <Button
              label={'Continue'}
              isLoading={isLoadingNewTransaction}
              onClick={handleOpenConfirmModal}
              buttonStyles={{
                height: '39px',
              }}
              containerStyles={{
                width: '100%',
              }}
            />
          </>
        )}
      </ContainerStyled>
    </>
  )
}
